import React, { useCallback, useEffect, useState } from 'react';
import { FiDownload, FiEye, FiLock, FiTrash2, FiUpload } from 'react-icons/fi';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import EmptyState from '../components/ui/EmptyState';
import { payslipsApi, Payslip } from '../api/payslips';
import toast from 'react-hot-toast';

const MesPaiePage: React.FC = () => {
  const [bulletins, setBulletins] = useState<Payslip[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<number[]>([]);
  const [downloading, setDownloading] = useState<number | null>(null);

  const loadBulletins = useCallback(async () => {
    setLoading(true);
    try {
      const res = await payslipsApi.listMine();
      const list = res.data.data?.payslips ?? [];
      setBulletins(list);
      if (list.length > 0) {
        setSelected(list.slice(0, Math.min(3, list.length)).map((b) => b.id));
      }
    } catch {
      toast.error('Impossible de charger vos bulletins de paie');
      setBulletins([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBulletins();
  }, [loadBulletins]);

  const toggle = (id: number) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const downloadBulletin = async (bulletin: Payslip, silent = false) => {
    setDownloading(bulletin.id);
    try {
      const res = await payslipsApi.download(bulletin.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = bulletin.original_filename || `${bulletin.title}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      if (!silent) {
        toast.success('Téléchargement lancé');
      }
    } catch {
      toast.error('Échec du téléchargement');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadSelection = async () => {
    const toDownload = bulletins.filter((b) => selected.includes(b.id));
    for (const bulletin of toDownload) {
      await downloadBulletin(bulletin, true);
    }
    if (toDownload.length > 0) {
      toast.success(`${toDownload.length} bulletin(s) téléchargé(s)`);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '3rem', display: 'flex', justifyContent: 'center' }}>
        <LoadingSpinner size={36} />
      </div>
    );
  }

  const dernier = bulletins[0];

  return (
    <div style={{ padding: '2rem', maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-2xl)', fontWeight: 700, color: 'var(--color-primary)' }}>
          Bulletins de paie
        </h1>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          <FiLock size={14} />
          Visible uniquement par vous
        </span>
      </div>

      {dernier ? (
        <Card padding="1.5rem" style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
          <p style={{ margin: '0 0 0.35rem', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
            Dernier bulletin de paie
          </p>
          <p style={{ margin: '0 0 0.25rem', fontWeight: 600, fontSize: 'var(--font-size-lg)' }}>{dernier.title}</p>
          {dernier.period_display && (
            <p style={{ margin: '0 0 1.25rem', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
              Période du {dernier.period_display}
            </p>
          )}
          <Button
            icon={<FiDownload size={16} />}
            onClick={() => downloadBulletin(dernier)}
            disabled={downloading === dernier.id}
          >
            Télécharger en PDF
          </Button>
        </Card>
      ) : (
        <Card padding="2rem" style={{ marginBottom: '1.5rem' }}>
          <EmptyState
            title="Aucun bulletin disponible"
            description="Vos bulletins seront déposés ici par le service RH dès qu'ils seront publiés."
          />
        </Card>
      )}

      {bulletins.length > 0 && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ margin: 0, fontSize: 'var(--font-size-base)', fontWeight: 600 }}>Vos documents disponibles</h2>
            {selected.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                icon={<FiDownload size={14} />}
                onClick={handleDownloadSelection}
              >
                Télécharger la sélection ({selected.length})
              </Button>
            )}
          </div>

          <Card padding="0">
            {bulletins.map((bulletin, index) => (
              <div
                key={bulletin.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '1rem 1.25rem',
                  borderBottom: index < bulletins.length - 1 ? '1px solid var(--color-border-light)' : 'none',
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(bulletin.id)}
                  onChange={() => toggle(bulletin.id)}
                  style={{ accentColor: 'var(--color-brand)' }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>{bulletin.title}</p>
                  {index === 0 && (
                    <span
                      style={{
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        color: 'var(--color-brand)',
                        background: 'var(--color-brand-light)',
                        padding: '0.15rem 0.5rem',
                        borderRadius: 'var(--radius-full)',
                      }}
                    >
                      Dernier bulletin
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => downloadBulletin(bulletin)}
                  title="Télécharger"
                  disabled={downloading === bulletin.id}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-brand)',
                    cursor: downloading === bulletin.id ? 'wait' : 'pointer',
                    padding: '0.25rem',
                    opacity: downloading === bulletin.id ? 0.5 : 1,
                  }}
                >
                  <FiDownload size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => downloadBulletin(bulletin)}
                  title="Consulter"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-text-muted)',
                    cursor: 'pointer',
                    padding: '0.25rem',
                  }}
                >
                  <FiEye size={18} />
                </button>
              </div>
            ))}
          </Card>
        </>
      )}

      <p style={{ marginTop: '1.5rem', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
        Vos bulletins sont déposés par le service RH. En cas de question, contactez votre responsable RH.
      </p>
    </div>
  );
};

export default MesPaiePage;
